import { jsx as _jsx } from "react/jsx-runtime";
import { useCombobox } from 'downshift';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useVirtual } from 'react-virtual';
import { isNumber } from '../../../common.types.js';
import { itemToString } from '../BKJComboBoxAsync.utils.js';
import { resolveNoResultsLabel, useBKJComboBoxAsyncContextHooks, useLoadingAnnouncer, } from './BKJComboBoxAsync.context.hooks.js';
const loadElement = (name) => ({
    type: 'Loader',
    id: 'Loader',
    value: 'Loader',
    key: 'Loader',
    name,
    disabled: true,
    label: 'Loader',
});
// WEB-4815 / R1: noResultsLabel overrides the existing i18next fallback
// when provided; when undefined, i18next.t('translations:NO_RESULTS') is
// used unchanged so French-locale consumers retain their translated text.
const noResultsElement = (name, noResultsLabel) => {
    const text = resolveNoResultsLabel(noResultsLabel);
    return {
        type: 'NoResults',
        id: 'No Results',
        value: text,
        key: 'No Results',
        name,
        disabled: true,
        label: text,
    };
};
export const BKJComboBoxAsyncContext = createContext({});
export const BKJComboBoxAsyncProvider = ({ icon, type, name, value, label, error, action, variant, options: __options__ = [], context, disabled, onChange, children, required, searchKey, placeholder, searchValue, onSearchChange, portalTargetElement, subLabelPosition, loadingLabel, resultsCountLabel, noResultsLabel, }) => {
    const { pageInfo, isLoading } = useContext(context);
    const hasNextPage = pageInfo?.hasNextPage;
    const [{ anchorElement, dropdownElement }, { setAnchorElement, setDropdownElement }] = useBKJComboBoxAsyncContextHooks();
    const isDropdownReverse = dropdownElement?.style?.top === 'auto' && dropdownElement?.style?.bottom === '0px';
    const options = useMemo(() => {
        let __o__ = [...__options__];
        if (hasNextPage || isLoading)
            __o__ = __o__.concat(loadElement(name));
        if (__o__.length === 0)
            __o__ = [noResultsElement(name, noResultsLabel)].concat(__o__);
        return __o__.map((opts, index) => {
            return { ...opts, index };
        });
    }, [__options__, hasNextPage, isLoading, name, noResultsLabel]);
    const parentRef = useRef(null);
    const rowVirtualizer = useVirtual({
        size: options.length,
        parentRef,
        keyExtractor: (index) => options[index].id,
    });
    const { reset, isOpen, openMenu, closeMenu, toggleMenu, selectedItem, getMenuProps, getItemProps, getInputProps, getLabelProps, getComboboxProps, highlightedIndex, setHighlightedIndex, getToggleButtonProps, setInputValue: __downShiftSetInputValue__, } = useCombobox({
        inputValue: searchValue,
        itemToString,
        items: options,
        initialSelectedItem: value,
        // WEB-4815 / T031 / FR-005b (inherited from WEB-4811 FR-005b): on every
        // open transition, highlight the selected option (if any) or the first
        // real option (skipping Group / Loader / NoResults). Mirrors WEB-4811's
        // stateReducer pattern in BKJComboBox.context.tsx.
        stateReducer: (state, actionAndChanges) => {
            const { changes } = actionAndChanges;
            const isOpening = !state.isOpen && changes.isOpen === true;
            if (isOpening && (changes.highlightedIndex ?? -1) < 0) {
                const activeSelectedItem = changes.selectedItem ?? state.selectedItem;
                const selectedIdx = activeSelectedItem
                    ? options.findIndex((opt) => String(opt.value) === String(activeSelectedItem.value))
                    : -1;
                const firstRealIdx = options.findIndex((opt) => !['Group', 'Loader', 'NoResults'].includes(opt.type));
                const desired = selectedIdx >= 0 ? selectedIdx : firstRealIdx;
                return { ...changes, highlightedIndex: desired };
            }
            return changes;
        },
        scrollIntoView: () => void 0,
        onHighlightedIndexChange: ({ highlightedIndex: __i__ = 0, type }) => {
            // WEB-4815 / T025 / I-A06: walk past Group/Loader/NoResults rows
            // in the arrow direction. If no real option is reachable (e.g.,
            // the listbox contains only NoResults), set INDEX = -1 so
            // aria-activedescendant does not reference a non-selectable row.
            // Previous implementation wrapped to 0, which landed on the
            // NoResults row when it was the only entry — violated I-A06.
            const SKIP_TYPES = ['Group', 'Loader', 'NoResults'];
            let INDEX = __i__;
            const highlightedItem = options[__i__];
            if (highlightedItem && SKIP_TYPES.includes(highlightedItem.type)) {
                const direction = type === '__input_keydown_arrow_up__' ? -1 : 1;
                let probe = __i__ + direction;
                while (probe >= 0 && probe < options.length && SKIP_TYPES.includes(options[probe].type)) {
                    probe += direction;
                }
                INDEX = probe >= 0 && probe < options.length ? probe : -1;
            }
            if (isNumber(INDEX) && INDEX >= 0) {
                rowVirtualizer.scrollToIndex(INDEX);
            }
            setHighlightedIndex(INDEX);
        },
        onSelectedItemChange: (event) => {
            const { selectedItem } = event;
            const nextValue = {
                ...selectedItem,
                name,
                value: selectedItem,
            };
            if (searchValue !== '') {
                __downShiftSetInputValue__('');
                onSearchChange({
                    target: {
                        value: '',
                        name: searchKey,
                    },
                });
            }
            if (onChange)
                onChange({
                    target: nextValue,
                });
            closeMenu();
            anchorElement?.blur();
            return void 0;
        },
        onIsOpenChange: ({ isOpen: __isOpen__, highlightedIndex: __highlightedIndex__, selectedItem: __selectedItem__, }) => {
            if (__isOpen__) {
                if (__selectedItem__) {
                    if (options.some((option) => {
                        return option.index === __selectedItem__.index;
                    }))
                        rowVirtualizer.scrollToIndex(__selectedItem__.index, { align: 'start' });
                }
            }
            if (!__isOpen__ && searchValue !== '') {
                __downShiftSetInputValue__('');
                onSearchChange({
                    target: {
                        value: '',
                        name: searchKey,
                    },
                });
            }
        },
    });
    // WEB-4815 / T011 / FR-002 / FR-003 / FR-004: derives aria-busy and the
    // live-region message for loading / results-count announcements.
    // optionsCount reads from __options__ (real consumer data) not from the
    // memoised options array which contains synthetic Loader / NoResults rows.
    const { liveMessage, isBusy } = useLoadingAnnouncer({
        isLoading: !!isLoading,
        isOpen,
        optionsCount: __options__.length,
        labels: { loadingLabel, resultsCountLabel },
    });
    const handleInputFocus = useCallback(openMenu, [openMenu]);
    // WEB-4815 / T032 / FR-005 (inherited): audit outcome.
    //
    // Pre-WEB-4815, this handler blurred the input on Escape+empty-search and
    // Enter+empty-search, causing Downshift's InputBlur to commit the
    // highlighted item. That silent commit was harmless before because
    // highlightedIndex was -1 on open (no highlight to commit). Now that the
    // WEB-4815 stateReducer implements the initial-highlight rule (FR-005b),
    // the first option is highlighted on open, and the blur-on-Escape
    // becomes a latent bug: pressing Escape commits the highlighted option
    // (violates FR-005 — Escape must not change selection).
    //
    // Resolution: keep the async search-clearing UX (first Escape clears the
    // search, second closes — product contract), but:
    //   - Escape path: do NOT blur. closeMenu() alone keeps focus on the
    //     input (WAI-ARIA APG editable combobox pattern) and prevents the
    //     spurious blur-commit.
    //   - Enter path: keep blur() — Enter SHOULD commit the highlighted
    //     option, and Downshift's InputBlur-commit is how that happens.
    const handleInputKeyDown = useCallback(({ key, nativeEvent }) => {
        switch (action) {
            case 'OpenToggle':
                switch (key) {
                    case 'Escape':
                        nativeEvent.preventDownshiftDefault = true;
                        if (isOpen && searchValue !== '')
                            onSearchChange({ target: { value: '', name: searchKey } });
                        if (isOpen && searchValue === '') {
                            closeMenu();
                            // No blur — WEB-4815 / FR-005: Escape must not commit
                            // the highlighted option.
                        }
                        break;
                    case 'Enter':
                        nativeEvent.preventDownshiftDefault = true;
                        if (isOpen && searchValue !== '')
                            onSearchChange({ target: { value: '', name: searchKey } });
                        if (isOpen && searchValue === '') {
                            closeMenu();
                            anchorElement?.blur();
                        }
                        break;
                }
                break;
            case 'ClearInputValue': {
                break;
            }
        }
    }, [action, anchorElement, closeMenu, isOpen, onSearchChange, searchKey, searchValue]);
    // WEB-4815: filter-auto-highlight effect. Previously this set
    // highlightedIndex=0 when searchValue was non-empty and index was -1,
    // which forced the NoResults row to be highlighted when it was the
    // only entry (violates I-A06 — feedback loop with onHighlightedIndexChange).
    // Now we only auto-highlight the FIRST REAL option's index, or leave
    // highlightedIndex at -1 if the options contain no real selectable rows.
    useEffect(() => {
        if (options.length === 0) {
            setHighlightedIndex(-1);
            return;
        }
        if (searchValue !== '' && highlightedIndex === -1) {
            const firstRealIdx = options.findIndex((opt) => !['Group', 'Loader', 'NoResults'].includes(opt.type));
            if (firstRealIdx >= 0)
                setHighlightedIndex(firstRealIdx);
        }
    }, [highlightedIndex, options, searchValue, setHighlightedIndex]);
    return (_jsx(BKJComboBoxAsyncContext.Provider, { value: {
            type,
            name,
            label,
            reset,
            value,
            error,
            action,
            isOpen,
            options,
            context,
            variant,
            onChange,
            required,
            disabled,
            openMenu,
            parentRef,
            searchKey,
            closeMenu,
            toggleMenu,
            searchValue,
            placeholder,
            selectedItem,
            getItemProps,
            getMenuProps,
            getInputProps,
            getLabelProps,
            anchorElement,
            rowVirtualizer,
            onSearchChange,
            dropdownElement,
            setAnchorElement,
            highlightedIndex,
            getComboboxProps,
            isDropdownReverse,
            setDropdownElement,
            portalTargetElement,
            setHighlightedIndex,
            getToggleButtonProps,
            onInputFocus: handleInputFocus,
            onInputKeyDown: handleInputKeyDown,
            leftIconProps: icon ? { iconName: icon } : undefined,
            subLabelPosition,
            liveMessage,
            isBusy,
            noResultsLabel,
        }, children: children }));
};
export const useBKJComboBoxAsyncContext = () => useContext(BKJComboBoxAsyncContext);
//# sourceMappingURL=BKJComboBoxAsync.context.js.map